import { RequestStatus } from 'typed/api';
import { Entity, NetworkArray } from 'typed/api/entities';
import { IAction } from './action';
import networkAction, { NetworkAction } from './networkAction';
import { EntityActionMap, IEntityActionCreatorSet, InferredActionType, PartialEntity, ProvidedKeys } from './entityActionSet';

export interface RootEntityActionCreatorSet<T extends string, E extends Entity> extends IEntityActionCreatorSet<T, E> {
    loadAll(...options: unknown[]): NetworkAction<T, void, NetworkArray<E>>;
    load(entityID: E['ID'], ...options: unknown[]): NetworkAction<T, { entityID: E['ID']; }, E>;
    addAll(entities: PartialEntity<E>[]): NetworkAction<T, { entities: PartialEntity<E>[]; }, void>;
    addAll(entities: PartialEntity<E>[], sync: true): NetworkAction<T, { entities: PartialEntity<E>[]; }, number[]>;
    add(entity: PartialEntity<E>): NetworkAction<T, { entity: PartialEntity<E>; }, void>;
    add(entity: PartialEntity<E>, sync: true, reloadAfterSync?: boolean, ...options: unknown[]): NetworkAction<T, { entity: PartialEntity<E>; }, number>;
    editAll(entities: PartialEntity<E>[], sync?: boolean): NetworkAction<T, { entities: PartialEntity<E>[]; }, void>;
    edit(entity: PartialEntity<E>, sync?: boolean, reloadAfterSync?: boolean, ...options: unknown[]): NetworkAction<T, { entity: PartialEntity<E>; }, void>;
    deleteAll(entities: E[]): NetworkAction<T, { entityIDs: E['ID'][]; }, void>;
    delete(entity: E, sync?: boolean): NetworkAction<T, { entityID: E['ID']; }, void>;
    voidAll(immediately?: true): IAction<T, { immediately: boolean; }>;
    void(entityID: E['ID'], immediately?: true): IAction<T, { immediately: boolean; entityID: E['ID']; }>;
}

export type RootEntityMappedActionCreators<E extends Entity, M extends EntityActionMap<any, E>> =
    { readonly [K in ProvidedKeys<M>]: RootEntityActionCreatorSet<InferredActionType<M, K>, E>[K] };

/**
 * @param entity a dummy parameter used to properly guess entity type, should be removed once API methods have proper types
*/
export default function rootEntityActionSet<E extends Entity, M extends EntityActionMap<any, E>>(_entity: E, map: M): { readonly [K in ProvidedKeys<M>]: RootEntityActionCreatorSet<InferredActionType<M, K>, E>[K] } {
    // The return type is actually MappedActionCreators<E, M>, but if result is declared this way, type hints/intellisense just show the name, which is not so helpful.
    // Explicit declaration here causes nice hints of all available methods, overloads etc.

    const
        addAllType = map.addAll && map.addAll.type!,
        addAllMethod = map.addAll && map.addAll.method!,
        addAllAction: (<T extends string, P>(entities: Partial<E>[]) => NetworkAction<T, P, number[]>) | null = addAllType && addAllMethod ?
            entities => networkAction(
                addAllType,
                { entityIDs: entities.map(({ ID }) => ID), entities },
                addAllMethod,
                entities
            ) :
            null,

        addType = map.add && map.add.type!,
        addMethod = map.add && map.add.method!,
        addAction: (<T extends string, P>(entity: Partial<E>) => NetworkAction<T, P, number>) | null = addType && addMethod ?
            entity => networkAction(addType, { entityID: entity.ID, entity }, addMethod, entity) :
            null,


        result = {} as unknown as RootEntityActionCreatorSet<string, E>;

    if (map.loadAll) {
        const
            type = map.loadAll.type!,
            method = map.loadAll.method!;

        result.loadAll = (...options) => networkAction(type, method, ...options);
    }

    if (map.load) {
        const
            type = map.load.type!,
            method = map.load.method!;

        result.load = (entityID, ...options) => networkAction(type, { entityID }, method, entityID, ...options);
    }

    if (map.addAll) {
        if (!addAllType || !addAllAction) {
            throw new Error('Can\'t happen.');
        }

        // TODO why types are not compatible?
        (result as any).addAll = (entities: PartialEntity<E>[], sync = false): NetworkAction<typeof addAllType, any, any> =>
            async(dispatch) => {
                dispatch({ type: addAllType, payload: { entityIDs: entities.map(({ ID }) => ID), entities, status: RequestStatus.UNSYNCED } });

                return !sync ? entities.map(({ ID }) => ID) : await dispatch(addAllAction(entities));
            };
    }


    if (map.editAll) {
        const
            type = map.editAll.type!,
            method = map.editAll.method!,
            editAllAction = (entities: PartialEntity<E>[]) => networkAction(type, { entityIDs: entities.map(({ ID }) => ID), entities }, method, entities);

        (result as any).editAll = (entities: PartialEntity<E>[], sync = false): NetworkAction<typeof type, any, any> =>
            async(dispatch) => {
                dispatch({ type, payload: { entityIDs: entities.map(({ ID }) => ID), entities, status: RequestStatus.UNSYNCED } });
                return !sync ? undefined : await dispatch(editAllAction(entities));
            };
    }

    if (map.add) {
        if (!addType || !addAction) {
            throw new Error('Can\'t happen.');
        }

        (result as any).add = (entity: PartialEntity<E>, sync = false, reloadAfterSync = false, ...reloadOptions: unknown[]): NetworkAction<typeof addType, any, any> =>
            async(dispatch) => {
                dispatch({ type: addType, payload: { entityID: entity.ID, entity, status: RequestStatus.UNSYNCED } });

                if (!sync) {
                    return entity.ID;
                }
                else if (!reloadAfterSync || !DEBUG && !result.load) {
                    return await dispatch(addAction(entity));
                }
                else {
                    if (result.load) {
                        const newID = await dispatch(addAction(entity));
                        return await dispatch(result.load(newID!, ...reloadOptions));
                    }

                    throw new Error('Can\'t reload after sync - methodsMap.load is not configured.');
                }
            };
    }

    if (map.edit) {
        const
            type = map.edit.type!,
            method = map.edit.method!,

            editAction = (entity: PartialEntity<E>) => networkAction(type, { entityID: entity.ID, entity }, method, entity);

        (result as any).edit = (entity: PartialEntity<E>, sync = false, reloadAfterSync = false, ...reloadOptions: unknown[]): NetworkAction<typeof type, any, any> =>
            async(dispatch) => {
                dispatch({
                    type,
                    payload: { entityID: entity.ID, entity, status: RequestStatus.UNSYNCED }
                });

                const syncAction =
                !entity.Custom || entity.Custom.status === RequestStatus.UNSYNCED ?
                    addAction as unknown as typeof editAction :
                    editAction;

                if (!sync) {
                    return undefined;
                }
                else if (!reloadAfterSync || !DEBUG && !result.load) {
                    return await dispatch(syncAction(entity));
                }
                else {
                    if (result.load) {
                        await dispatch(syncAction(entity));
                        return await dispatch(result.load(entity.ID, ...reloadOptions));
                    }

                    throw new Error('Can\'t reload after sync - methodsMap.load is not configured.');
                }
            };
    }

    if (map.deleteAll) {
        const
            type = map.deleteAll.type!,
            method = map.deleteAll.method!;

        // TODO this code works if entity successfully synced or not synced at all
        // but it will fail to delete entity if sync request failed or not yet finished
        // latter situation is somewhat fixable by building request queue
        // but how to delete entity from DB if it has been added but its new ID has been lost in network failure?
        result.deleteAll = entities => async(dispatch) => {
            const
                localEntityIDs = entities.filter(({ Custom: { status } }) => status === RequestStatus.UNSYNCED).map(({ ID }) => ID),
                remoteEntityIDs = entities.filter(({ Custom: { status } }) => status !== RequestStatus.UNSYNCED).map(({ ID }) => ID);

            if (localEntityIDs.length) {
                dispatch({ type, payload: { status: RequestStatus.SUCCEEDED, entityIDs: localEntityIDs, response: undefined } });
            }
            if (remoteEntityIDs.length) {
                await dispatch(networkAction(type, { entityIDs: remoteEntityIDs }, method, remoteEntityIDs));
            }
        };
    }

    if (map.delete) {
        const
            type = map.delete.type!,
            method = map.delete.method!;

        // TODO this code works if entity successfully synced or not synced at all
        // but it will fail to delete entity if sync request failed or not yet finished
        // latter situation is somewhat fixable by building request queue
        // but how to delete entity from DB if it has been added but its new ID has been lost in network failure?
        result.delete = (entity, sync = true) => async(dispatch) => {
            if (!sync || entity.Custom.status === RequestStatus.UNSYNCED) {
                dispatch({ type, payload: { status: RequestStatus.SUCCEEDED, entityID: entity.ID, response: undefined } });
            }
            else {
                await dispatch(networkAction(type, { entityID: entity.ID }, method, entity.ID));
            }
        };
    }

    if (map.voidAll) {
        const { type } = map.voidAll;
        result.voidAll = (immediately = true) => ({ type, payload: { immediately } });
    }

    if (map.void) {
        const { type } = map.void;
        result.void = (entityID, immediately = true) => ({ type, payload: { entityID, immediately } });
    }

    return result as unknown as RootEntityMappedActionCreators<E, M>;
}

